import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode
} from "react";
import {
  Check,
  CircleDot,
  CircleHelp,
  Flower2,
  Leaf,
  Lightbulb,
  Minus,
  Plus,
  RotateCcw,
  Sailboat,
  Shell,
  Sprout,
  Star,
  type LucideIcon
} from "lucide-react";

import type {
  ActivityAdapter,
  ActivityCompletionMetrics,
  ActivityExecutionContext
} from "@/app/integration/contracts";
import { B4_MATH_MARKET_APPLE_ASSET } from "@/assets/requirements/math/b4-math-player-assets";
import type {
  MathLevel,
  PatternToken
} from "@/content/math";
import type { LevelDefinition } from "@/domain";
import {
  type CurriculumAudioPlayer,
  useCurriculumFeedbackAudio
} from "@/features/audio";
import {
  AudioReplayButton,
  CurriculumVoiceButton,
  PrimaryCommandButton
} from "@/ui";

import {
  FOREST_MARKET_PLAYER_ADAPTER,
  MATH_GRID_DIRECTION_LABELS,
  MATH_PATTERN_TOKEN_LABELS,
  NUMBER_TRAIN_PLAYER_ADAPTER,
  PATTERN_BRIDGE_PLAYER_ADAPTER,
  type MathPlayerAction,
  type MathPlayerRenderModel,
  type MathPlayerState,
  type MathPlayerVisual,
  type MathRenderChoice
} from "./math-player-adapters";
import "./math-level-player.css";

const DEFAULT_EXECUTION_CONTEXT: ActivityExecutionContext = {
  nowMs: Date.now,
  reducedMotion: false
};

const UNAVAILABLE_MATH_AUDIO_PLAYER: CurriculumAudioPlayer = {
  activeVoiceId: null,
  isPlayingPath: () => false,
  playPath: async () => false,
  stopOwnedVoice: () => undefined
};

const PATTERN_TOKEN_ICONS: Readonly<
  Record<PatternToken, LucideIcon>
> = {
  acorn: CircleDot,
  flower: Flower2,
  leaf: Leaf,
  mushroom: Sprout,
  shell: Shell,
  star: Star
};

const DIRECTION_GLYPHS = {
  up: "↑",
  right: "→",
  down: "↓",
  left: "←"
} as const;

export interface MathLevelPlayerProps {
  readonly audioPlayer?: CurriculumAudioPlayer;
  readonly executionContext?: ActivityExecutionContext;
  readonly level: MathLevel;
  readonly onComplete?: (metrics: ActivityCompletionMetrics) => void;
  readonly onReplay?: () => void;
}

interface MathLevelPlayerSessionProps<TPayload>
  extends Omit<MathLevelPlayerProps, "level"> {
  readonly adapter: ActivityAdapter<
    TPayload,
    MathPlayerState,
    MathPlayerAction,
    MathPlayerRenderModel
  >;
  readonly level: LevelDefinition<TPayload>;
}

interface MathChoiceEvents {
  readonly onChoiceKeyDown: (
    event: KeyboardEvent<HTMLButtonElement>,
    choiceId: string
  ) => void;
  readonly onSelectChoice: (choiceId: string) => void;
  readonly registerChoiceRef: (
    choiceId: string,
    element: HTMLButtonElement | null
  ) => void;
}

interface MathChoiceButtonProps extends MathChoiceEvents {
  readonly choice: MathRenderChoice;
  readonly routeCell?: boolean;
}

function RepeatedStars({ count }: { readonly count: number }) {
  return (
    <span aria-hidden="true" className="math-level-player__token-grid">
      {Array.from({ length: count }, (_, index) => (
        <Star key={index} />
      ))}
    </span>
  );
}

function RepeatedMarketItems({
  count,
  itemId
}: {
  readonly count: number;
  readonly itemId: string;
}) {
  return (
    <span
      aria-hidden="true"
      className="math-level-player__token-grid math-level-player__token-grid--market"
      data-item={itemId}
    >
      {Array.from({ length: count }, (_, index) => (
        <span
          className="math-level-player__market-token"
          data-market-item={itemId}
          key={index}
        >
          {itemId === "apple" ? (
            <img
              alt=""
              className="math-level-player__market-image"
              draggable={false}
              src={B4_MATH_MARKET_APPLE_ASSET}
            />
          ) : null}
        </span>
      ))}
    </span>
  );
}

function PatternTokenDisplay({
  token
}: {
  readonly token: PatternToken | null;
}) {
  if (token === null) {
    return (
      <>
        <CircleHelp aria-hidden="true" />
        <span>空位</span>
      </>
    );
  }

  const TokenIcon = PATTERN_TOKEN_ICONS[token];

  return (
    <>
      <TokenIcon aria-hidden="true" />
      <span>{MATH_PATTERN_TOKEN_LABELS[token]}</span>
    </>
  );
}

function ChoiceDisplay({ choice }: { readonly choice: MathRenderChoice }) {
  switch (choice.display.kind) {
    case "stars":
      return <RepeatedStars count={choice.display.count} />;
    case "market-item":
      return (
        <RepeatedMarketItems
          count={choice.display.count}
          itemId={choice.display.itemId}
        />
      );
    case "pattern-token":
      return <PatternTokenDisplay token={choice.display.token} />;
    case "route-position":
      return <CircleDot aria-hidden="true" />;
  }
}

function MathChoiceButton({
  choice,
  onChoiceKeyDown,
  onSelectChoice,
  registerChoiceRef,
  routeCell = false
}: MathChoiceButtonProps) {
  return (
    <button
      aria-label={"选择" + choice.label}
      aria-pressed={choice.selected}
      className={
        "math-level-player__choice" +
        (routeCell ? " math-level-player__choice--route" : "")
      }
      data-choice-id={choice.id}
      data-display-kind={choice.display.kind}
      disabled={choice.disabled}
      onClick={() => onSelectChoice(choice.id)}
      onKeyDown={(event) => onChoiceKeyDown(event, choice.id)}
      ref={(element) => {
        registerChoiceRef(choice.id, element);
      }}
      type="button"
    >
      <ChoiceDisplay choice={choice} />
      {choice.display.kind === "pattern-token" ? (
        <span className="math-level-player__choice-label">
          {choice.label}
        </span>
      ) : null}
    </button>
  );
}

interface MathActivityBoardProps extends MathChoiceEvents {
  readonly model: MathPlayerRenderModel;
}

function NumberTrainBoard({
  visual
}: {
  readonly visual: Extract<
    MathPlayerVisual,
    { kind: "number-train" }
  >;
}) {
  return (
    <div
      aria-label={
        visual.mode === "match"
          ? "站台上有" +
            String(visual.targetCount) +
            "颗星星"
          : visual.relation === "most"
            ? "比较最多的星星"
            : "比较最少的星星"
      }
      className="math-level-player__train-board"
      role="group"
    >
      <span
        aria-hidden="true"
        className="math-level-player__train-engine"
      >
        <CircleDot />
      </span>
      {visual.targetCount !== null ? (
        <div className="math-level-player__station-count">
          <RepeatedStars count={visual.targetCount} />
        </div>
      ) : (
        <strong className="math-level-player__relation">
          {visual.relation === "most" ? "找最多" : "找最少"}
        </strong>
      )}
      <span
        aria-hidden="true"
        className="math-level-player__rail"
      />
    </div>
  );
}

function ForestMarketBoard({
  visual
}: {
  readonly visual: Extract<
    MathPlayerVisual,
    { kind: "forest-market" }
  >;
}) {
  const OperationIcon = visual.operation === "add" ? Plus : Minus;
  const operationLabel =
    visual.operation === "add" ? "放进" : "拿走";

  return (
    <div
      aria-label={
        "原来有" +
        String(visual.startingCount) +
        visual.itemUnit +
        visual.itemLabel +
        "，" +
        operationLabel +
        String(visual.changeCount) +
        visual.itemUnit +
        visual.itemLabel
      }
      className="math-level-player__market-board"
      role="group"
    >
      <div className="math-level-player__market-group">
        <span className="math-level-player__group-label">原来</span>
        <RepeatedMarketItems
          count={visual.startingCount}
          itemId={visual.itemId}
        />
      </div>
      <OperationIcon
        aria-label={operationLabel}
        className="math-level-player__operation"
      />
      <div
        className="math-level-player__market-group"
        data-operation={visual.operation}
      >
        <span className="math-level-player__group-label">
          {operationLabel}
        </span>
        <RepeatedMarketItems
          count={visual.changeCount}
          itemId={visual.itemId}
        />
      </div>
    </div>
  );
}

function PatternSequenceBoard({
  visual
}: {
  readonly visual: Extract<
    MathPlayerVisual,
    { kind: "pattern-sequence" }
  >;
}) {
  return (
    <ol
      aria-label="图案规律"
      className="math-level-player__pattern-sequence"
    >
      {visual.tokens.map((token, index) => (
        <li
          aria-label={
            "第" +
            String(index + 1) +
            "格，" +
            (token === null
              ? "空位"
              : MATH_PATTERN_TOKEN_LABELS[token])
          }
          data-token={token ?? "empty"}
          key={"pattern-slot-" + String(index + 1)}
        >
          <PatternTokenDisplay token={token} />
        </li>
      ))}
    </ol>
  );
}

function findRouteChoice(
  choices: readonly MathRenderChoice[],
  row: number,
  column: number
): MathRenderChoice | undefined {
  return choices.find(
    (choice) =>
      choice.display.kind === "route-position" &&
      choice.display.position.row === row &&
      choice.display.position.column === column
  );
}

function PatternRouteBoard({
  model,
  onChoiceKeyDown,
  onSelectChoice,
  registerChoiceRef
}: MathActivityBoardProps) {
  if (model.visual.kind !== "pattern-route") {
    return null;
  }

  const visual = model.visual;
  const cellCount = visual.columns * visual.rows;

  return (
    <div className="math-level-player__route-board">
      <h3>{model.choicePrompt}</h3>
      <ol
        aria-label="行走顺序"
        className="math-level-player__moves"
      >
        {visual.moves.map((move, index) => (
          <li
            aria-label={
              "第" +
              String(index + 1) +
              "步，向" +
              MATH_GRID_DIRECTION_LABELS[move]
            }
            key={"route-move-" + String(index + 1)}
          >
            <span aria-hidden="true">{DIRECTION_GLYPHS[move]}</span>
          </li>
        ))}
      </ol>
      <div
        aria-label="路线方格"
        className="math-level-player__route-grid"
        role="group"
        style={{
          gridTemplateColumns:
            "repeat(" +
            String(visual.columns) +
            ", minmax(0, 1fr))"
        }}
      >
        {Array.from({ length: cellCount }, (_, index) => {
          const row = Math.floor(index / visual.columns);
          const column = index % visual.columns;
          const choice = findRouteChoice(model.choices, row, column);
          const isStart =
            visual.start.row === row && visual.start.column === column;

          return (
            <div
              className="math-level-player__route-cell"
              data-choice={choice !== undefined}
              data-start={isStart}
              key={"route-cell-" + String(row) + "-" + String(column)}
            >
              {isStart ? (
                <Sailboat
                  aria-label="小船起点"
                  className="math-level-player__boat"
                />
              ) : null}
              {choice !== undefined && !model.completed ? (
                <MathChoiceButton
                  choice={choice}
                  onChoiceKeyDown={onChoiceKeyDown}
                  onSelectChoice={onSelectChoice}
                  registerChoiceRef={registerChoiceRef}
                  routeCell
                />
              ) : null}
              {choice !== undefined && model.completed ? (
                <span
                  aria-label={
                    choice.selected ? "选中的终点" : "备选终点"
                  }
                  className="math-level-player__route-marker"
                  data-selected={choice.selected}
                >
                  {choice.selected ? <Check /> : <CircleDot />}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MathActivityBoard({
  model,
  onChoiceKeyDown,
  onSelectChoice,
  registerChoiceRef
}: MathActivityBoardProps) {
  let content: ReactNode;

  switch (model.visual.kind) {
    case "number-train":
      content = <NumberTrainBoard visual={model.visual} />;
      break;
    case "forest-market":
      content = <ForestMarketBoard visual={model.visual} />;
      break;
    case "pattern-sequence":
      content = <PatternSequenceBoard visual={model.visual} />;
      break;
    case "pattern-route":
      content = (
        <PatternRouteBoard
          model={model}
          onChoiceKeyDown={onChoiceKeyDown}
          onSelectChoice={onSelectChoice}
          registerChoiceRef={registerChoiceRef}
        />
      );
      break;
  }

  return <div className="math-level-player__board">{content}</div>;
}

function MathChoicePanel({
  model,
  onChoiceKeyDown,
  onSelectChoice,
  registerChoiceRef
}: MathActivityBoardProps) {
  if (model.visual.kind === "pattern-route" || model.completed) {
    return null;
  }

  return (
    <section
      aria-label={model.choicePrompt}
      className="math-level-player__choice-panel"
    >
      <h3>{model.choicePrompt}</h3>
      <div className="math-level-player__choices" role="group">
        {model.choices.map((choice) => (
          <MathChoiceButton
            choice={choice}
            key={choice.id}
            onChoiceKeyDown={onChoiceKeyDown}
            onSelectChoice={onSelectChoice}
            registerChoiceRef={registerChoiceRef}
          />
        ))}
      </div>
    </section>
  );
}

function getNavigationDirection(
  key: string
): "next" | "previous" | null {
  switch (key) {
    case "ArrowRight":
    case "ArrowDown":
      return "next";
    case "ArrowLeft":
    case "ArrowUp":
      return "previous";
    default:
      return null;
  }
}

function MathLevelPlayerSession<TPayload>({
  adapter,
  audioPlayer = UNAVAILABLE_MATH_AUDIO_PLAYER,
  executionContext = DEFAULT_EXECUTION_CONTEXT,
  level,
  onComplete,
  onReplay
}: MathLevelPlayerSessionProps<TPayload>) {
  const titleId = useId();
  const instructionId = useId();
  const resultId = useId();
  const payload = useMemo(
    () => adapter.parsePayload(level.payload),
    [adapter, level.payload]
  );
  const [state, setState] = useState<MathPlayerState>(() =>
    adapter.createInitialState(payload, executionContext)
  );
  const choiceRefs = useRef(new Map<string, HTMLButtonElement>());
  const completionReported = useRef(false);
  const replayFocusPending = useRef(false);
  const resultHeadingRef = useRef<HTMLHeadingElement>(null);

  const dispatch = useCallback(
    (action: MathPlayerAction) => {
      setState((currentState) =>
        adapter.reduce(
          currentState,
          payload,
          action,
          executionContext
        )
      );
    },
    [adapter, executionContext, payload]
  );

  const model = useMemo(
    () => adapter.toRenderModel(state, payload),
    [adapter, payload, state]
  );
  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    model.hint
  );
  const metrics = useMemo(
    () => adapter.getMetrics(state, payload),
    [adapter, payload, state]
  );
  const firstEnabledChoiceId =
    model.choices.find(({ disabled }) => !disabled)?.id ?? null;

  useEffect(() => {
    if (!metrics.completed) {
      completionReported.current = false;
      return;
    }

    if (!completionReported.current) {
      completionReported.current = true;
      onComplete?.(metrics);
    }
  }, [metrics, onComplete]);

  useEffect(() => {
    if (metrics.completed) {
      resultHeadingRef.current?.focus();
    }
  }, [metrics.completed]);

  useEffect(() => {
    if (
      state.lastInputMethod === "keyboard" &&
      state.focusedChoiceId !== null &&
      !metrics.completed
    ) {
      choiceRefs.current.get(state.focusedChoiceId)?.focus();
    }
  }, [
    metrics.completed,
    state.focusedChoiceId,
    state.lastInputMethod
  ]);

  useEffect(() => {
    if (
      !metrics.completed &&
      replayFocusPending.current &&
      firstEnabledChoiceId !== null
    ) {
      replayFocusPending.current = false;
      choiceRefs.current.get(firstEnabledChoiceId)?.focus();
    }
  }, [firstEnabledChoiceId, metrics.completed]);

  const registerChoiceRef = useCallback(
    (choiceId: string, element: HTMLButtonElement | null) => {
      if (element === null) {
        choiceRefs.current.delete(choiceId);
      } else {
        choiceRefs.current.set(choiceId, element);
      }
    },
    []
  );

  const onSelectChoice = useCallback(
    (choiceId: string) => {
      dispatch({
        type: "select-choice",
        choiceId,
        inputMethod: "pointer"
      });
    },
    [dispatch]
  );

  const onChoiceKeyDown = useCallback(
    (
      event: KeyboardEvent<HTMLButtonElement>,
      choiceId: string
    ) => {
      const direction = getNavigationDirection(event.key);

      if (direction !== null) {
        event.preventDefault();
        dispatch({
          type: "move-focus",
          currentChoiceId: choiceId,
          direction
        });
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        dispatch({
          type: "select-choice",
          choiceId,
          inputMethod: "keyboard"
        });
      }
    },
    [dispatch]
  );

  const handlePromptReplay = useCallback(() => {
    if (level.promptAudio === undefined) {
      return;
    }

    dispatch({ type: "replay-prompt" });
    void audioPlayer.playPath(level.promptAudio);
  }, [audioPlayer, dispatch, level.promptAudio]);

  const handleReplay = useCallback(() => {
    replayFocusPending.current = true;
    dispatch({ type: "reset" });
    onReplay?.();
  }, [dispatch, onReplay]);

  return (
    <section
      aria-labelledby={titleId}
      className="math-level-player"
      data-activity-type={level.activityType}
      data-reduced-motion={executionContext.reducedMotion}
    >
      <header className="math-level-player__header">
        <div className="math-level-player__heading">
          <p className="math-level-player__domain">
            数学岛 · 第 {String(level.difficulty)} 关
          </p>
          <h2 id={titleId}>{level.title}</h2>
        </div>
        <AudioReplayButton
          disabled={level.promptAudio === undefined}
          isPlaying={
            level.promptAudio !== undefined &&
            audioPlayer.isPlayingPath(level.promptAudio)
          }
          onClick={handlePromptReplay}
        />
      </header>

      <p
        className="math-level-player__instruction"
        data-caption="true"
        id={instructionId}
      >
        {level.instruction}
      </p>

      <div aria-describedby={instructionId}>
        <MathActivityBoard
          model={model}
          onChoiceKeyDown={onChoiceKeyDown}
          onSelectChoice={onSelectChoice}
          registerChoiceRef={registerChoiceRef}
        />
      </div>

      <MathChoicePanel
        model={model}
        onChoiceKeyDown={onChoiceKeyDown}
        onSelectChoice={onSelectChoice}
        registerChoiceRef={registerChoiceRef}
      />

      {!model.completed ? (
        <div className="math-level-player__actions">
          <PrimaryCommandButton
            icon={Lightbulb}
            onClick={() => dispatch({ type: "request-hint" })}
          >
            给我提示
          </PrimaryCommandButton>
        </div>
      ) : null}

      <div
        aria-live="polite"
        className="math-level-player__feedback"
        data-hint-stage={model.hint?.stage ?? "none"}
        data-feedback-kind={model.hint?.kind ?? "none"}
      >
        {model.hint !== null ? (
          <div className="math-level-player__hint-row">
            <p
              className="math-level-player__hint"
              data-stage={model.hint.stage}
              role="status"
            >
              {model.hint.text}
            </p>
            <CurriculumVoiceButton
              isPlaying={feedbackAudio.isPlaying}
              label={model.hint.text}
              onPlay={feedbackAudio.replay}
            />
          </div>
        ) : null}

        {model.result !== null ? (
          <section
            aria-labelledby={resultId}
            className="math-level-player__result"
          >
            <h3
              id={resultId}
              ref={resultHeadingRef}
              tabIndex={-1}
            >
              {model.result.heading}
            </h3>
            <p role="status">{model.result.message}</p>
            <PrimaryCommandButton
              icon={RotateCcw}
              onClick={handleReplay}
            >
              再玩一次
            </PrimaryCommandButton>
          </section>
        ) : null}
      </div>
    </section>
  );
}

function assertNeverMathLevel(level: never): never {
  throw new Error("Unsupported math activity " + JSON.stringify(level));
}

export function MathLevelPlayer({
  audioPlayer,
  executionContext,
  level,
  onComplete,
  onReplay
}: MathLevelPlayerProps) {
  switch (level.activityType) {
    case "number-train":
      return (
        <MathLevelPlayerSession
          adapter={NUMBER_TRAIN_PLAYER_ADAPTER}
          audioPlayer={audioPlayer}
          executionContext={executionContext}
          key={level.id}
          level={level}
          onComplete={onComplete}
          onReplay={onReplay}
        />
      );
    case "forest-market":
      return (
        <MathLevelPlayerSession
          adapter={FOREST_MARKET_PLAYER_ADAPTER}
          audioPlayer={audioPlayer}
          executionContext={executionContext}
          key={level.id}
          level={level}
          onComplete={onComplete}
          onReplay={onReplay}
        />
      );
    case "pattern-bridge":
      return (
        <MathLevelPlayerSession
          adapter={PATTERN_BRIDGE_PLAYER_ADAPTER}
          audioPlayer={audioPlayer}
          executionContext={executionContext}
          key={level.id}
          level={level}
          onComplete={onComplete}
          onReplay={onReplay}
        />
      );
    default:
      return assertNeverMathLevel(level);
  }
}
